import { useState, useEffect } from 'react';
import { User, Car, CarFront, Ticket } from 'lucide-react';
import activityService from '../services/activityService';
import { LineChart } from '../components/statistics/charts';
import '../components/statistics/Statistics.css';
import './Activity.css';

const Activity = () => {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const data = await activityService.getStats(days);
      setStats(data);
      setError('');
    } catch (err: any) {
      console.error('Fetch stats error:', err);
      setError(err.response?.data?.error?.message || err.message || 'Failed to load activity statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (value > 0 && value <= 365) {
      setDays(value);
    }
  };

  if (loading) {
    return (
      <div className="activity-container">
        <div className="loading">Loading activity data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="activity-container">
        <div className="error-message">{error}</div>
        <button onClick={fetchStats} className="btn-retry">Retry</button>
      </div>
    );
  }

  return (
    <div className="activity-container">
      <div className="activity-header">
        <div>
          <h1>Platform Activity</h1>
          <p className="activity-subtitle">
            The last {days} days in Israel time, today included. Staff accounts are left out; riders and drivers are
            counted once each over the whole period.
          </p>
        </div>
        <div className="days-selector">
          <label htmlFor="days-input">Days:</label>
          <input
            id="days-input"
            type="number"
            min="1"
            max="365"
            value={days}
            onChange={handleDaysChange}
            className="days-input"
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="activity-stat-card">
          <div className="stat-icon"><User size={24} /></div>
          <div className="stat-info">
            <div className="stat-label">Riders (requested a seat)</div>
            <div className="stat-value">{stats?.totals?.users || 0}</div>
          </div>
        </div>

        <div className="activity-stat-card">
          <div className="stat-icon"><Car size={24} /></div>
          <div className="stat-info">
            <div className="stat-label">Drivers (posted a ride)</div>
            <div className="stat-value">{stats?.totals?.drivers || 0}</div>
          </div>
        </div>

        <div className="activity-stat-card">
          <div className="stat-icon"><CarFront size={24} /></div>
          <div className="stat-info">
            <div className="stat-label">Rides posted</div>
            <div className="stat-value">{stats?.totals?.rides || 0}</div>
          </div>
        </div>

        <div className="activity-stat-card">
          <div className="stat-icon"><Ticket size={24} /></div>
          <div className="stat-info">
            <div className="stat-label">Seat requests</div>
            <div className="stat-value">{stats?.totals?.bookings || 0}</div>
          </div>
        </div>
      </div>

      {/* Line Chart */}
      <div className="chart-container">
        <h2>Activity Trend</h2>
        <div className="stats-theme">
          <LineChart
            data={stats?.dailyData || []}
            xKey="date"
            series={[
              { key: 'users', label: 'Riders' },
              { key: 'drivers', label: 'Drivers' },
              { key: 'rides', label: 'Rides posted' },
              { key: 'bookings', label: 'Seat requests' },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default Activity;
